// Gantt calculation utilities
import { Task, ViewMode } from '../types/gantt.types';

// Calculate task position on timeline
export const calculateTaskPosition = (
  task: Task,
  timelineStart: Date,
  dayWidth: number
) => {
  const startOffset = Math.floor(
    (task.start_date.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24)
  );
  const duration = task.duration || 1;
  
  return {
    x: startOffset * dayWidth,
    width: duration * dayWidth
  };
};

// Get date range for timeline
export const getDateRange = (tasks: Task[], viewMode: ViewMode) => {
  if (tasks.length === 0) {
    const now = new Date();
    return {
      startDate: now,
      endDate: addMonths(now, 1),
      totalDays: 30
    };
  }

  // Find min and max dates
  let minDate = new Date(tasks[0].start_date);
  let maxDate = new Date(tasks[0].end_date);

  tasks.forEach(task => {
    if (task.start_date < minDate) minDate = new Date(task.start_date);
    if (task.end_date > maxDate) maxDate = new Date(task.end_date);
  });

  // Add padding based on view mode
  let paddedStart = new Date(minDate);
  let paddedEnd = new Date(maxDate);

  switch (viewMode) {
    case 'day':
      paddedStart.setDate(paddedStart.getDate() - 3);
      paddedEnd.setDate(paddedEnd.getDate() + 3);
      break;
    case 'week':
      paddedStart.setDate(paddedStart.getDate() - 7);
      paddedEnd.setDate(paddedEnd.getDate() + 7);
      break;
    case 'month':
      paddedStart.setMonth(paddedStart.getMonth() - 1);
      paddedEnd.setMonth(paddedEnd.getMonth() + 1);
      break;
    case 'quarter':
      paddedStart.setMonth(paddedStart.getMonth() - 3);
      paddedEnd.setMonth(paddedEnd.getMonth() + 3);
      break;
    case 'year':
      paddedStart.setFullYear(paddedStart.getFullYear() - 1);
      paddedEnd.setFullYear(paddedEnd.getFullYear() + 1);
      break;
  }

  const totalDays = Math.ceil(
    (paddedEnd.getTime() - paddedStart.getTime()) / (1000 * 60 * 60 * 24)
  );

  return {
    startDate: paddedStart,
    endDate: paddedEnd,
    totalDays
  };
};

// Format date for header based on view mode
export const formatDateHeader = (date: Date, viewMode: ViewMode): string => {
  const options: Intl.DateTimeFormatOptions = {};
  
  switch (viewMode) {
    case 'day':
      return date.getDate().toString();
    case 'week':
      options.day = '2-digit';
      options.month = 'short';
      return date.toLocaleDateString('pl-PL', options);
    case 'month':
      options.day = '2-digit';
      return date.toLocaleDateString('pl-PL', options);
    case 'quarter':
      options.month = 'short';
      return date.toLocaleDateString('pl-PL', options);
    case 'year':
      options.month = 'short';
      options.year = '2-digit';
      return date.toLocaleDateString('pl-PL', options);
    default:
      return date.toLocaleDateString('pl-PL');
  }
};

// Check if date is weekend
export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

// Get working days between two dates
export const getWorkingDays = (
  startDate: Date,
  endDate: Date,
  workingDays: number[] = [1, 2, 3, 4, 5],
  holidays: Date[] = []
): number => {
  let count = 0;
  const current = new Date(startDate);
  
  while (current <= endDate) {
    const dayOfWeek = current.getDay();
    const isHoliday = holidays.some(
      holiday => holiday.toDateString() === current.toDateString()
    );
    
    if (workingDays.includes(dayOfWeek) && !isHoliday) {
      count++;
    }
    
    current.setDate(current.getDate() + 1);
  }
  
  return count;
};

// Calculate task duration in working days
export const calculateTaskDuration = (
  startDate: Date,
  endDate: Date,
  workingDays: number[] = [1, 2, 3, 4, 5],
  holidays: Date[] = []
): number => {
  return getWorkingDays(startDate, endDate, workingDays, holidays);
};

// Calculate critical path using CPM algorithm
export const calculateCriticalPath = (tasks: Task[]): string[] => {
  // Build adjacency list
  const graph: Record<string, string[]> = {};
  const inDegree: Record<string, number> = {};
  const earliestStart: Record<string, number> = {};
  const earliestFinish: Record<string, number> = {};
  const latestStart: Record<string, number> = {};
  const latestFinish: Record<string, number> = {};
  
  // Initialize
  tasks.forEach(task => {
    graph[task.id] = [];
    inDegree[task.id] = 0;
    earliestStart[task.id] = 0;
    earliestFinish[task.id] = task.duration;
  });
  
  // Build graph from dependencies
  tasks.forEach(task => {
    if (task.dependencies) {
      task.dependencies.forEach(dep => {
        if (dep.type === 'FS') { // Finish-to-Start
          graph[dep.task_id].push(dep.dependent_task_id);
          inDegree[dep.dependent_task_id]++;
        }
      });
    }
  });
  
  // Forward pass - calculate earliest times
  const queue: string[] = [];
  Object.keys(inDegree).forEach(taskId => {
    if (inDegree[taskId] === 0) {
      queue.push(taskId);
    }
  });
  
  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentTask = tasks.find(t => t.id === current)!;
    
    graph[current].forEach(successor => {
      const successorTask = tasks.find(t => t.id === successor)!;
      earliestStart[successor] = Math.max(
        earliestStart[successor],
        earliestFinish[current]
      );
      earliestFinish[successor] = earliestStart[successor] + successorTask.duration;
      
      inDegree[successor]--;
      if (inDegree[successor] === 0) {
        queue.push(successor);
      }
    });
  }
  
  // Find project completion time
  let projectDuration = 0;
  Object.values(earliestFinish).forEach(time => {
    projectDuration = Math.max(projectDuration, time);
  });
  
  // Backward pass - calculate latest times
  tasks.forEach(task => {
    latestFinish[task.id] = projectDuration;
    latestStart[task.id] = projectDuration - task.duration;
  });
  
  // Calculate slack and identify critical path
  const criticalTasks: string[] = [];
  tasks.forEach(task => {
    const slack = latestStart[task.id] - earliestStart[task.id];
    if (slack === 0) {
      criticalTasks.push(task.id);
    }
  });
  
  return criticalTasks;
};

// Helper function to add months
const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

// Calculate resource utilization
export const calculateResourceUtilization = (
  tasks: Task[],
  resources: any[],
  startDate: Date,
  endDate: Date
): Record<string, number> => {
  const utilization: Record<string, number> = {};
  
  resources.forEach(resource => {
    const resourceTasks = tasks.filter(task => 
      task.assigned_to?.includes(resource.id)
    );
    
    let totalHours = 0;
    resourceTasks.forEach(task => {
      totalHours += task.estimated_hours || (task.duration * 8);
    });
    
    const availableHours = getWorkingDays(startDate, endDate) * (resource.capacity || 8);
    utilization[resource.id] = (totalHours / availableHours) * 100;
  });
  
  return utilization;
};

// Auto-schedule tasks based on dependencies
export const autoScheduleTasks = (tasks: Task[]): Task[] => {
  const scheduledTasks = [...tasks];
  const taskMap = new Map<string, Task>();
  
  scheduledTasks.forEach(task => {
    taskMap.set(task.id, task);
  });
  
  // Sort tasks by dependencies
  const sortedTasks = topologicalSort(scheduledTasks);
  
  // Schedule each task
  sortedTasks.forEach(task => {
    if (task.dependencies && task.dependencies.length > 0) {
      let latestPredecessorEnd = new Date(0);
      
      task.dependencies.forEach(dep => {
        const predecessor = taskMap.get(dep.task_id);
        if (predecessor) {
          const predecessorEnd = new Date(predecessor.end_date);
          if (dep.lag) {
            predecessorEnd.setDate(predecessorEnd.getDate() + dep.lag);
          }
          
          if (predecessorEnd > latestPredecessorEnd) {
            latestPredecessorEnd = predecessorEnd;
          }
        }
      });
      
      // Update task dates
      task.start_date = new Date(latestPredecessorEnd);
      task.end_date = new Date(task.start_date);
      task.end_date.setDate(task.end_date.getDate() + task.duration);
    }
  });
  
  return scheduledTasks;
};

// Topological sort for task scheduling
const topologicalSort = (tasks: Task[]): Task[] => {
  const sorted: Task[] = [];
  const visited = new Set<string>();
  const visiting = new Set<string>();
  
  const visit = (task: Task) => {
    if (visiting.has(task.id)) {
      throw new Error('Circular dependency detected');
    }
    
    if (!visited.has(task.id)) {
      visiting.add(task.id);
      
      // Visit dependencies first
      if (task.dependencies) {
        task.dependencies.forEach(dep => {
          const depTask = tasks.find(t => t.id === dep.dependent_task_id);
          if (depTask) {
            visit(depTask);
          }
        });
      }
      
      visiting.delete(task.id);
      visited.add(task.id);
      sorted.push(task);
    }
  };
  
  tasks.forEach(task => {
    if (!visited.has(task.id)) {
      visit(task);
    }
  });
  
  return sorted;
};